import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldAlert, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Multi-floor plan viewer for buyers.
 * Falls back to legacy floor_plan_images if floor_plans array is empty.
 */
const FloorPlansSection = ({ property }) => {
  const hasStructured = Array.isArray(property.floor_plans) && property.floor_plans.length > 0;
  const floors = hasStructured
    ? property.floor_plans.filter(f => f.enhanced_url || f.original_url)
    : (property.floor_plan_images || []).map((url, idx) => ({
        floor_label: `Floor Plan ${idx + 1}`,
        enhanced_url: url,
        floor_order: idx,
        show_original_to_buyers: false,
        original_url: null,
      }));

  const [activeIdx, setActiveIdx] = useState(0);
  const [viewMode, setViewMode] = useState('enhanced'); // 'enhanced' | 'original'
  const [disclaimer, setDisclaimer] = useState('');

  useEffect(() => {
    const country = (property.country || 'UK').toUpperCase();
    axios.get(`${API_URL}/api/ai/floor-labels`, { params: { country } })
      .then(r => setDisclaimer(r.data.disclaimer || ''))
      .catch(() => {});
  }, [property.country]);

  if (floors.length === 0) return null;
  const active = floors[activeIdx] || floors[0];
  const canToggleOriginal = active?.show_original_to_buyers && active?.original_url;
  const currentSrc = (viewMode === 'original' && canToggleOriginal) ? active.original_url : active.enhanced_url;

  return (
    <div className="luxury-card p-6" data-testid="floor-plan-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Floor Plans</h2>
        {canToggleOriginal && (
          <div className="flex items-center gap-1 text-xs">
            <button type="button" onClick={() => setViewMode('enhanced')}
              className={`px-2.5 py-1 border transition-colors ${viewMode === 'enhanced' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-500 border-slate-200'}`}
              data-testid="view-enhanced-btn">
              AI Architect
            </button>
            <button type="button" onClick={() => setViewMode('original')}
              className={`px-2.5 py-1 border transition-colors flex items-center gap-1 ${viewMode === 'original' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-500 border-slate-200'}`}
              data-testid="view-original-btn">
              <Eye className="h-3 w-3" /> Owner's Original
            </button>
          </div>
        )}
      </div>

      {/* Floor tabs */}
      {floors.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-slate-100">
          {floors.map((f, idx) => (
            <button key={`f-${idx}`} type="button" onClick={() => { setActiveIdx(idx); setViewMode('enhanced'); }}
              className={`px-3 py-1.5 text-xs font-medium border transition-colors ${idx === activeIdx ? 'bg-[#E07A3A] text-white border-[#E07A3A]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#E07A3A]'}`}
              data-testid={`buyer-floor-tab-${idx}`}>
              {f.floor_label}
            </button>
          ))}
        </div>
      )}

      {/* Plan image */}
      {currentSrc && (
        <div className="border border-slate-100 bg-white p-2 mb-3" data-testid="floorplan-image">
          <img src={currentSrc} alt={`${active.floor_label} plan`} className="w-full h-auto" loading="lazy" />
        </div>
      )}

      {/* Country-specific legal disclaimer */}
      {disclaimer && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 leading-relaxed" data-testid="floorplan-legal-disclaimer">
          <ShieldAlert className="h-3.5 w-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
          <span>{disclaimer}</span>
        </div>
      )}
    </div>
  );
};

export default FloorPlansSection;
